import { yupResolver } from '@hookform/resolvers/yup';
import pluralize from 'pluralize';
import { type ChangeEvent, memo, useCallback, useId, useMemo } from 'react';
import { Controller, type ControllerRenderProps, FormProvider, useForm, useWatch } from 'react-hook-form';
import { type InferType, mixed, number, object, string } from 'yup';

import { type Clip } from '@air/api/types';
import { useSavedSizesCatalog } from '@air/feature-saved-sizes/use-saved-sizes-catalog';
import { type SavedSize } from '@air/feature-saved-sizes/utils';
import { Banner } from '@air/primitive-banner';
import { Button } from '@air/primitive-button';
import { Field, FieldLabel } from '@air/primitive-field';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { RadioGroup, RadioGroupItem } from '@air/primitive-radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';

import { BatchExportExactSizeControls } from '~/components/ExportImagesModal/components/BatchExportExactSizeControls';
import {
  type BatchExportClip,
  type CropAnchor,
  calculateExportDimensions,
  type ResizeMode,
  useBatchExportImages,
} from '~/components/ExportImagesModal/hooks/useBatchExportImages';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { getBestImgixExportSource, isClipExportable } from '~/utils/Imgix';

const MAX_BATCH_EXPORT_CLIPS = 20;

const SCALE_OPTIONS = [
  { label: '1x (Original)', value: '1' },
  { label: '.75x', value: '0.75' },
  { label: '.5x', value: '0.5' },
  { label: '.25x', value: '0.25' },
] as const;

const FORMAT_OPTIONS = [
  { label: 'JPG', value: 'jpg' },
  { label: 'PNG', value: 'png' },
  { label: 'WEBP', value: 'webp' },
] as const;

const RESIZE_MODE_OPTIONS = [
  { label: 'Scale', testId: 'RESIZE_MODE_SCALE', value: 'scale' },
  { label: 'Exact size', testId: 'RESIZE_MODE_EXACT_SIZE', value: 'exactSize' },
] as const;

const TARGET_SIZE_ERROR_MESSAGE = 'Please enter a valid size';
// Stable identity so downstream size-select memoization doesn't see a new array on every render.
const NO_SAVED_SIZES: SavedSize[] = [];

const createBatchExportImagesFormValidationSchema = (exactSizeExportEnabled: boolean) =>
  object({
    resizeMode: mixed<ResizeMode>().oneOf(['scale', 'exactSize']).required().default('scale'),
    scale: string().required(),
    format: string().required(),
    suffix: string().optional().default(''),
    cropAnchor: mixed<CropAnchor>().oneOf(['center', 'corner']).required().default('center'),
    longestSide: number()
      .typeError(TARGET_SIZE_ERROR_MESSAGE)
      .integer(TARGET_SIZE_ERROR_MESSAGE)
      .positive(TARGET_SIZE_ERROR_MESSAGE)
      .when('resizeMode', ([resizeMode], schema) =>
        exactSizeExportEnabled && resizeMode === 'scale'
          ? schema.required(TARGET_SIZE_ERROR_MESSAGE)
          : schema.optional(),
      ),
    targetWidth: number()
      .typeError(TARGET_SIZE_ERROR_MESSAGE)
      .integer(TARGET_SIZE_ERROR_MESSAGE)
      .positive(TARGET_SIZE_ERROR_MESSAGE)
      .when('resizeMode', ([resizeMode], schema) =>
        resizeMode === 'exactSize' ? schema.required(TARGET_SIZE_ERROR_MESSAGE) : schema.optional(),
      ),
    targetHeight: number()
      .typeError(TARGET_SIZE_ERROR_MESSAGE)
      .integer(TARGET_SIZE_ERROR_MESSAGE)
      .positive(TARGET_SIZE_ERROR_MESSAGE)
      .when('resizeMode', ([resizeMode], schema) =>
        resizeMode === 'exactSize' ? schema.required(TARGET_SIZE_ERROR_MESSAGE) : schema.optional(),
      ),
  });

export type BatchExportImagesFormOutput = InferType<ReturnType<typeof createBatchExportImagesFormValidationSchema>>;

export type BatchExportImagesFormInput = {
  resizeMode: ResizeMode;
  scale: string;
  format: string;
  suffix: string;
  cropAnchor: CropAnchor;
  longestSide: number | undefined;
  targetWidth: number | undefined;
  targetHeight: number | undefined;
};

export type BatchExportImagesModalProps = AirModalProps<{
  clips: Clip[];
  /** Runs the multi-select canvas crop handoff. This modal closes after it. Omit when the surface cannot open canvas. */
  onCropAndEditOnCanvas?: () => void;
  /** Omit when mounting from a public/share-link surface with no workspace context. */
  workspaceId?: string;
}>;

export const BatchExportImagesModal = memo(
  ({ clips, onClose, onCropAndEditOnCanvas, workspaceId }: BatchExportImagesModalProps) => {
    const formId = useId();
    const { exportImages } = useBatchExportImages();
    const { exactSizeExportEnabled, savedSizesEnabled } = useTemporaryFeatureFlags();
    const { sizePresets: catalogSizePresets } = useSavedSizesCatalog({ enabled: savedSizesEnabled, workspaceId });
    // useSavedSizesCatalog({ enabled: false }) only stops the query from refetching — it keeps serving whatever
    // this query key last cached, including sizes cached by another surface, so the flag must also be checked here.
    const sizePresets = savedSizesEnabled ? catalogSizePresets : NO_SAVED_SIZES;

    const { compatibleClips, skippedCount, overCapCount } = useMemo(() => {
      const compatible = clips.filter((clip) => isClipExportable(clip));
      const overCap = Math.max(0, compatible.length - MAX_BATCH_EXPORT_CLIPS);

      return {
        compatibleClips: compatible.slice(0, MAX_BATCH_EXPORT_CLIPS),
        skippedCount: clips.length - compatible.length,
        overCapCount: overCap,
      };
    }, [clips]);

    const batchClips = useMemo<BatchExportClip[]>(
      () =>
        compatibleClips
          .map((clip) => {
            const exportSource = getBestImgixExportSource(clip.ext, clip.assets);

            if (!exportSource) {
              return undefined;
            }

            return {
              id: clip.id,
              imageSrc: exportSource,
              fileName: clip.title || clip.importedName || clip.id,
              height: clip.height,
              width: clip.width,
            };
          })
          .filter((clip): clip is BatchExportClip => !!clip),
      [compatibleClips],
    );

    const resolver = useMemo(
      () => yupResolver(createBatchExportImagesFormValidationSchema(exactSizeExportEnabled)),
      [exactSizeExportEnabled],
    );

    const methods = useForm<BatchExportImagesFormInput, unknown, BatchExportImagesFormOutput>({
      mode: 'onChange',
      defaultValues: {
        resizeMode: 'scale',
        scale: '1',
        format: 'jpg',
        suffix: '',
        cropAnchor: 'center',
        longestSide: undefined,
        targetWidth: undefined,
        targetHeight: undefined,
      },
      resolver,
    });

    const {
      control,
      formState: { errors, isValid },
      handleSubmit,
      setValue,
    } = methods;

    const suffix = useWatch({ control, name: 'suffix' });
    const format = useWatch({ control, name: 'format' });
    const resizeMode = useWatch({ control, name: 'resizeMode' });
    const scale = useWatch({ control, name: 'scale' });
    const cropAnchor = useWatch({ control, name: 'cropAnchor' });
    const targetWidth = useWatch({ control, name: 'targetWidth' });
    const targetHeight = useWatch({ control, name: 'targetHeight' });
    const longestSide = useWatch({ control, name: 'longestSide' });

    const handleLongestSideChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setValue('longestSide', event.target.value === '' ? undefined : Number(event.target.value), {
          shouldValidate: true,
        });
      },
      [setValue],
    );

    const renderScaleField = useCallback(
      ({ field }: { field: ControllerRenderProps<BatchExportImagesFormInput, 'scale'> }) => (
        <Select onValueChange={field.onChange} value={field.value}>
          <SelectTrigger className="w-full" id={`${formId}-scale`} />
          <SelectContent>
            {SCALE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
      [formId],
    );

    const renderResizeModeField = useCallback(
      ({ field }: { field: ControllerRenderProps<BatchExportImagesFormInput, 'resizeMode'> }) => (
        <RadioGroup
          aria-labelledby={`${formId}-resizeMode-label`}
          className="flex-row gap-4"
          onValueChange={field.onChange}
          value={field.value}
        >
          {RESIZE_MODE_OPTIONS.map((option) => (
            <RadioGroupItem
              data-testid={option.testId}
              id={`${formId}-resizeMode-${option.value}`}
              key={option.value}
              value={option.value}
            >
              {option.label}
            </RadioGroupItem>
          ))}
        </RadioGroup>
      ),
      [formId],
    );

    const renderSuffixField = useCallback(
      ({ field }: { field: ControllerRenderProps<BatchExportImagesFormInput, 'suffix'> }) => (
        <Input
          {...field}
          autoComplete="off"
          className="w-full"
          id={`${formId}-suffix`}
          placeholder="e.g. _final"
          value={field.value ?? ''}
        />
      ),
      [formId],
    );

    const renderFormatField = useCallback(
      ({ field }: { field: ControllerRenderProps<BatchExportImagesFormInput, 'format'> }) => (
        <Select onValueChange={field.onChange} value={field.value}>
          <SelectTrigger className="w-full" id={`${formId}-format`} />
          <SelectContent>
            {FORMAT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
      [formId],
    );

    const hasTargetSizeError = !!errors.targetWidth || !!errors.targetHeight;

    const cappedCount = useMemo(() => {
      const hasValidTargetSize = !hasTargetSizeError && targetWidth != null && targetHeight != null;

      if (resizeMode !== 'exactSize' || !hasValidTargetSize) {
        return 0;
      }

      return batchClips.filter(
        (clip) =>
          calculateExportDimensions(clip.width, clip.height, {
            resizeMode: 'exactSize',
            scale,
            targetWidth: targetWidth ?? 0,
            targetHeight: targetHeight ?? 0,
            cropAnchor,
          }).wasCapped,
      ).length;
    }, [batchClips, hasTargetSizeError, resizeMode, scale, targetWidth, targetHeight, cropAnchor]);

    const exportButtonLabel = useMemo(() => {
      const count = batchClips.length;

      if (count === 0) {
        return 'Export';
      }

      return `Export ${pluralize('asset', count, true)}`;
    }, [batchClips.length]);

    const handleCropAndEditOnCanvas = useCallback(() => {
      if (!onCropAndEditOnCanvas) {
        return;
      }

      onCropAndEditOnCanvas();
      onClose();
    }, [onClose, onCropAndEditOnCanvas]);

    const scaleModeField = exactSizeExportEnabled ? (
      <Field>
        <FieldLabel htmlFor={`${formId}-longestSide`}>Set longest side dimension</FieldLabel>
        <Input
          autoComplete="off"
          className="w-full"
          hasError={!!errors.longestSide}
          id={`${formId}-longestSide`}
          onChange={handleLongestSideChange}
          type="number"
          value={longestSide ?? ''}
        />
        {errors.longestSide ? (
          <div className="mt-1 ads:text-error text-12 text-red-9">{errors.longestSide.message}</div>
        ) : (
          longestSide != null && (
            <div className="mt-2 ads:text-metadata text-12 text-gray-10">
              Each asset will be scaled so its longest side is {longestSide}px, maintaining its original aspect ratio.
              Assets smaller than this size will not be upscaled.
            </div>
          )
        )}
      </Field>
    ) : (
      <Field>
        <FieldLabel htmlFor={`${formId}-scale`}>Scale</FieldLabel>
        <Controller control={control} name="scale" render={renderScaleField} />
      </Field>
    );

    return (
      <Modal isOpen onDismiss={onClose}>
        <FormProvider {...methods}>
          <header className="mb-6 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <ModalTitle>Export {pluralize('asset', batchClips.length, true)} as</ModalTitle>
              <ModalCloseButton className="shrink-0" onClick={onClose} />
            </div>
            {skippedCount > 0 && (
              <Banner appearance="warning" size="small">
                {pluralize('asset', skippedCount, true)} in your selection {skippedCount === 1 ? 'is' : 'are'} not an
                exportable format and will be skipped.
              </Banner>
            )}
            {overCapCount > 0 && (
              <Banner appearance="info" size="small">
                Only the first {MAX_BATCH_EXPORT_CLIPS} assets will be exported.
              </Banner>
            )}
          </header>
          <form
            id={formId}
            onSubmit={handleSubmit((values) => {
              exportImages(batchClips, {
                resizeMode: values.resizeMode,
                scale: values.scale,
                targetWidth: values.targetWidth ?? 0,
                targetHeight: values.targetHeight ?? 0,
                cropAnchor: values.cropAnchor,
                format: values.format,
                suffix: values.suffix ?? '',
                longestSide: values.longestSide,
              });
              onClose();
            })}
          >
            <div className="flex flex-col gap-4">
              {exactSizeExportEnabled && (
                <Field>
                  <FieldLabel id={`${formId}-resizeMode-label`}>Resize by</FieldLabel>
                  <Controller control={control} name="resizeMode" render={renderResizeModeField} />
                </Field>
              )}

              {resizeMode === 'scale' ? (
                scaleModeField
              ) : (
                <BatchExportExactSizeControls
                  cappedCount={cappedCount}
                  formId={formId}
                  sizePresets={sizePresets}
                  totalCount={batchClips.length}
                />
              )}

              <Field>
                <FieldLabel htmlFor={`${formId}-suffix`}>Suffix (optional)</FieldLabel>
                <Controller control={control} name="suffix" render={renderSuffixField} />
                <div className="mt-2 break-all ads:text-metadata text-12 text-gray-10">{`Preview: {filename}${suffix}.${format}`}</div>
              </Field>
              <Field>
                <FieldLabel htmlFor={`${formId}-format`}>Format</FieldLabel>
                <Controller control={control} name="format" render={renderFormatField} />
              </Field>
            </div>
          </form>
          <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
            {onCropAndEditOnCanvas ? (
              <Button
                appearance="outline"
                className="mr-auto"
                color="blue"
                data-testid="EXPORT_IMAGES_MODAL_CROP_AND_EDIT_ON_CANVAS_BUTTON"
                onClick={handleCropAndEditOnCanvas}
                size="large"
                type="button"
              >
                Crop and edit on Canvas
              </Button>
            ) : null}
            <div className="flex shrink-0 gap-3">
              <Button appearance="ghost" color="gray" onClick={onClose} size="large" type="button">
                Cancel
              </Button>
              <Button
                appearance="filled"
                color="blue"
                data-testid="EXPORT_IMAGES_MODAL_EXPORT_BUTTON"
                disabled={batchClips.length === 0 || !isValid}
                form={formId}
                size="large"
                type="submit"
              >
                {exportButtonLabel}
              </Button>
            </div>
          </div>
        </FormProvider>
      </Modal>
    );
  },
);

BatchExportImagesModal.displayName = 'BatchExportImagesModal';
